/**
 * "What could this line become" for all 8 grid lines (3 rows, 3 columns, 2
 * diagonals) — live, per-line, as cells reveal, not just the single overall
 * best hand (see useBestHandsSoFar for that). Uses
 * HandEvaluator.evaluateLinePotential/evaluateJewelLinePotential, which
 * handle partially-revealed lines with a heuristic and fully-revealed lines
 * with the real evaluator.
 */

import { useMemo } from 'react';
import {
  HandEvaluator,
  isRealCard,
  GRID_LINES,
  playerLines,
  withoutSpecials,
  type Card,
  type GridPosition,
  type LinePotential,
  type JewelLinePotential,
} from '@pokercubed/shared';
import { useGameStore } from '../store/gameStore';

export interface LineRowPotential {
  line: GridPosition[];
  rank: LinePotential;
  jewel: JewelLinePotential;
  /** False when a special card (effects on) takes this line away from the player, e.g. Crossed's diagonals. */
  available: boolean;
}

export function useLinePotentials(): LineRowPotential[] {
  const communityCards = useGameStore((state) => state.communityCards);
  const players = useGameStore((state) => state.players);
  const yourSeat = useGameStore((state) => state.yourSeat);
  const specialEffects = useGameStore((state) => state.specialCardEffects);

  return useMemo(() => {
    if (yourSeat === null) return [];

    const you = players.find((p) => p.seatPosition === yourSeat);
    if (!you?.holeCards) return [];

    const realHoleCards = you.holeCards.filter(isRealCard);
    if (realHoleCards.length !== 2) return [];
    // The cards as they actually play: plain while special effects are off.
    const holeCards = specialEffects ? realHoleCards : withoutSpecials(realHoleCards);
    const allowed = playerLines(realHoleCards, specialEffects);
    const isAllowed = (line: readonly GridPosition[]) =>
      allowed.some((l) => l.length === line.length && l.every((pos, i) => pos === line[i]));

    const cardAtPosition = new Map(
      communityCards.map((c) => [c.position, c.revealed ? c.card : null] as const)
    );

    // Every card already visible to the viewer — used to exclude cards no
    // longer in the deck when a line's best-case search (evaluateLinePotential
    // for a line with exactly one cell left) picks a candidate.
    const visibleCards: Card[] = [
      ...holeCards,
      ...communityCards.filter((c) => c.revealed && c.card).map((c) => c.card as Card),
    ];

    return GRID_LINES.map((line) => {
      const cells = line.map((position) => ({
        position,
        card: cardAtPosition.get(position) ?? null,
      }));

      return {
        line: [...line],
        rank: HandEvaluator.evaluateLinePotential(holeCards, [...line], cells, visibleCards),
        jewel: HandEvaluator.evaluateJewelLinePotential(holeCards, [...line], cells, visibleCards),
        available: isAllowed(line),
      };
    });
  }, [communityCards, players, yourSeat, specialEffects]);
}
